import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { registerAppNavigation, unregisterAppNavigation } from "@/app/navigation/appNavigation";
import { loadingService } from "@/shared/loading/loading.service";
import { routes } from "./routes";
import {
  LoginPage,
  SignupPage,
  ForgotPasswordPage,
  LandingPage,
  EmailVerificationPendingPage,
} from "@/features/auth/view";
import { DashboardPage } from "@/features/dashboard/view";
import { PropertiesPage, PropertiesSearchPage } from "@/features/properties/view";
import { ProtestPaymentPage } from "@/features/protest/view/protestPayment/ProtestPaymentPage";
import { SelectPropertyTypePage } from "@/features/protest/view/selectPropertyType/SelectProperty/SelectPropertyTypePage";
import { AuthenticatedLayoutWrapper } from "@/app/layouts/AuthenticatedLayoutWrapper";
import { NonAuthenticatedLayout } from "@/app/layouts/NonAuthenticatedLayout";
import { AuthLayoutWrapper } from "@/app/layouts/AuthLayoutWrapper";
// import { PropertiesLayoutWrapper } from "@/app/layouts/PropertiesLayoutWrapper";
import { HomePage } from "@/features/home/view/HomePage";
import { HomesteadExemptionPage } from "@/features/homesteadExemption/view/HomeSteadExcemption/HomesteadExemptionPage";
import { SettingsLayout } from "@/app/layouts/settingsLayout/SettingsLayout";
import { CommunityNewsFeedPage } from "@/features/news/view/CommunityNewsFeedPage";
import {
  SettingsAccountPage,
  SettingsHomePage,
  SettingsPropertiesPage,
  SettingsProtestHistoryPage,
  SettingsAutopaySubscriptionPage,
  SettingsHistoricalAchievementsPage,
  SettingsAppSettingsPage,
  SettingsContactUsPage,
  SettingsContactUsChatPage,
  SettingsLogoutPage,
} from "@/features/settings";
import { ReferAndEarnPage } from "@/features/referrals";
import { NotificationsPage } from "@/features/notifications/view/NotificationPage/NotificationsPage";
import { AndyNotificationsPage } from "@/features/notifications/view/ChatNotification/AndyNotificationsPage";
import { ChatNotificationsPage } from "@/features/notifications/view/ChatNotification/ChatNotificationsPage";
import { PowerCardPlans } from "@/features/powerBill/views/powerCardPlans";
import { EnergyBotScreen } from "@/features/powerBill/views/headlessEnroll";
import { ForceMobileUpdatePage } from "@/features/auth/view/ForceMobileUpdate/ForceMobileUpdatePage";
import { ForcePasswordUpdatePage } from "@/features/auth/view/ForcePasswordUpdate/ForcePasswordUpdatePage";
import { EmailVerificationRequiredPage } from "@/features/auth/view/EmailVerificationRequired/EmailVerificationRequiredPage";
import { PhoneVerificationPage } from "@/features/auth/view/PhoneVerification/PhoneVerificationPage";
import { PhoneMobileInputPage } from "@/features/auth/view/PhoneMobileInput/PhoneMobileInputPage";
/**
 * Router component - loader appears briefly on route changes and hides after page loads
 */
const RouterContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isInitialMount, setIsInitialMount] = useState(true);

  useEffect(() => {
    registerAppNavigation(navigate);
    return () => unregisterAppNavigation();
  }, [navigate]);

  useEffect(() => {
    // Skip showing loader on initial mount
    if (isInitialMount) {
      setIsInitialMount(false);
      loadingService.reset();
      return;
    }

    // Show loader briefly on route change
    loadingService.show("central");

    // Hide loader after page has loaded
    // Using requestAnimationFrame ensures DOM is ready before hiding
    const hideLoader = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Use reset to ensure loader is completely cleared
          loadingService.reset();
        });
      });
    };

    // Hide loader after a short delay to ensure page has rendered
    const timer = setTimeout(hideLoader, 150);

    return () => {
      clearTimeout(timer);
    };
  }, [location.pathname, isInitialMount]);

  return (
    <Routes>
      <Route element={<AuthenticatedLayoutWrapper />}>
        <Route path={routes.homepage} element={<HomePage />} />
        <Route path={routes.powerCardPlans} element={<PowerCardPlans />} />
        <Route path={routes.energyBotScreen} element={<EnergyBotScreen />} />

        {/* Protected routes - redirect to login if not authenticated */}
        <Route path={routes.dashboard} element={<DashboardPage />} />
        {/* <Route path={routes.properties} element={<PropertiesPage />} /> */}
        <Route path={routes.propertiesSearch} element={<PropertiesSearchPage />} />
        <Route path={routes.selectPropertyType} element={<SelectPropertyTypePage />} />
        <Route path={routes.protestPayment} element={<ProtestPaymentPage />} />

        <Route path={routes.homesteadExemption} element={<HomesteadExemptionPage />} />
        <Route path={routes.newsFeed} element={<CommunityNewsFeedPage />} />
        <Route path={routes.referAndEarn} element={<ReferAndEarnPage />} />
        <Route path={routes.notifications} element={<NotificationsPage />} />
        <Route path={routes.andyNotifications} element={<AndyNotificationsPage />} />
        <Route path={routes.chatNotifications} element={<ChatNotificationsPage />} />
        <Route path={routes.settings} element={<SettingsLayout />}>
          <Route index element={<Navigate to="account" replace />} />
          <Route path="account" element={<SettingsAccountPage />} />
          <Route path="home" element={<SettingsHomePage />} />
          <Route path="properties" element={<SettingsPropertiesPage />} />
          <Route path="protest-history" element={<SettingsProtestHistoryPage />} />
          <Route path="autopay-subscription" element={<SettingsAutopaySubscriptionPage />} />
          <Route path="historical-achievements" element={<SettingsHistoricalAchievementsPage />} />
          <Route path="app-settings" element={<SettingsAppSettingsPage />} />
          <Route path="contact-us" element={<SettingsContactUsPage />} />
          <Route path="contact-us/chat" element={<SettingsContactUsChatPage />} />
          <Route path="logout" element={<SettingsLogoutPage />} />
        </Route>
      </Route>

      {/* Public routes - accessible to both authenticated and non-authenticated users */}
      {/* Non Authenticated */}
      <Route element={<NonAuthenticatedLayout />}>
        <Route path={routes.home} element={<LandingPage />} />
        <Route path={routes.landing} element={<LandingPage />} />
        <Route path={routes.properties} element={<PropertiesPage />} />
      </Route>

      {/* Authenticated */}
      <Route element={<AuthLayoutWrapper />}>
        <Route path={routes.login} element={<LoginPage />} />
        <Route path={routes.signup} element={<SignupPage />} />
        <Route path={routes.forgotPassword} element={<ForgotPasswordPage />} />
        <Route path={routes.emailVerificationPending} element={<EmailVerificationPendingPage />} />
        <Route path={routes.forceMobileUpdate} element={<ForceMobileUpdatePage />} />
        <Route path={routes.forcePasswordUpdate} element={<ForcePasswordUpdatePage />} />
        <Route
          path={routes.emailVerificationRequired}
          element={<EmailVerificationRequiredPage />}
        />
        <Route path={routes.phoneVerification} element={<PhoneVerificationPage />} />
        <Route path={routes.phoneMobileInput} element={<PhoneMobileInputPage />} />
      </Route>
    </Routes>
  );
};

export const RouterWithLoader = () => {
  return (
    <BrowserRouter>
      <RouterContent />
    </BrowserRouter>
  );
};
